import { Command } from 'commander';
import { withErrorHandling } from '../utils/error-handler';
import { ClaudeMonitor, ClaudeController } from '@gongfu/claude-integration';
import { logger } from '../utils/logger';
import pc from 'picocolors';
import { spawn } from 'child_process';
import path from 'path';
import fs from 'fs-extra';

export function createClaudeCommand(): Command {
  const command = new Command('claude');
  
  command
    .description('Claude Code integration and control')
    .alias('cc');

  // Log Claude activity
  command
    .command('log')
    .description('Log Claude Code activity')
    .option('--type <type>', 'Activity type', 'general')
    .option('--data <data>', 'Activity data')
    .action(withErrorHandling(async (options) => {
      const monitor = new ClaudeMonitor();
      await monitor.logActivity(options.type, options.data);
    }));

  // Monitor Claude activity
  command
    .command('monitor')
    .description('Start monitoring Claude Code activity')
    .option('--output <format>', 'Output format (json|text|sse)', 'text')
    .option('--port <port>', 'SSE server port', '3002')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🔍 Starting Claude Code Monitor\n'));
      
      const monitor = new ClaudeMonitor({
        outputFormat: options.output,
        ssePort: parseInt(options.port)
      });
      
      await monitor.start();
      
      console.log(pc.green('✅ Monitor started'));
      console.log(pc.dim(`Output format: ${options.output}`));
      if (options.output === 'sse') {
        console.log(pc.dim(`SSE endpoint: http://localhost:${options.port}/claude/events`));
      }
      
      // Keep process running
      process.on('SIGINT', async () => {
        console.log('\nStopping monitor...');
        await monitor.stop();
        process.exit(0);
      });
    }));

  // Start Claude Code with task
  command
    .command('start')
    .description('Start Claude Code with a specific task')
    .option('--task <task>', 'Task description or ID')
    .option('--project <path>', 'Project path', process.cwd())
    .option('--mode <mode>', 'Mode (interactive|background)', 'interactive')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🚀 Starting Claude Code\n'));
      
      const controller = new ClaudeController({
        projectRoot: options.project
      });
      
      // Prepare task
      if (options.task) {
        await controller.assignTask(options.task);
        console.log(pc.green(`✅ Task assigned: ${options.task}`));
      }
      
      // Start Claude Code
      if (options.mode === 'interactive') {
        // Direct spawn for interactive mode
        const claudeProcess = spawn('claude', ['code'], {
          cwd: options.project,
          stdio: 'inherit',
          env: {
            ...process.env,
            GONGFU_TASK_MODE: '1'
          }
        });
        
        claudeProcess.on('error', (err) => {
          console.error(pc.red('Failed to start Claude Code:'), err.message);
          console.log(pc.yellow('Make sure Claude Code is installed and in PATH'));
        });
        
        claudeProcess.on('exit', (code) => {
          console.log(pc.dim(`Claude Code exited with code ${code}`));
        });
      } else {
        // Background mode
        await controller.startInBackground();
        console.log(pc.green('✅ Claude Code started in background'));
        console.log(pc.dim('Check .claude/current-task.md for task details'));
      }
    }));

  // Send task to Claude
  command
    .command('task')
    .description('Send a task to Claude Code')
    .argument('<task>', 'Task description or ID')
    .option('--priority <priority>', 'Task priority', 'medium')
    .option('--wait', 'Wait for task completion')
    .action(withErrorHandling(async (task, options) => {
      console.log(pc.bold('\n📋 Sending task to Claude Code\n'));
      
      const controller = new ClaudeController();
      
      // Assign task
      const taskId = await controller.assignTask(task, {
        priority: options.priority
      });
      
      console.log(pc.green(`✅ Task assigned: ${taskId}`));
      console.log(pc.dim('Task written to .claude/current-task.md'));
      
      if (options.wait) {
        console.log(pc.dim('\nWaiting for task completion...'));
        
        // Monitor task progress
        const monitor = new ClaudeMonitor();
        monitor.on('task-completed', (data) => {
          console.log(pc.green('\n✅ Task completed!'));
          console.log(pc.dim(`Duration: ${data.duration}`));
          process.exit(0);
        });
        
        await monitor.start();
      }
    }));

  // Check Claude status
  command
    .command('status')
    .description('Check Claude Code status and current task')
    .action(withErrorHandling(async () => {
      const controller = new ClaudeController();
      const status = await controller.getStatus();
      
      console.log(pc.bold('\n📊 Claude Code Status\n'));
      
      if (status.isActive) {
        console.log(pc.green('● Active'));
        
        if (status.currentTask) {
          console.log(pc.bold('\nCurrent Task:'));
          console.log(`  ID: ${status.currentTask.id}`);
          console.log(`  Content: ${status.currentTask.content}`);
          console.log(`  Started: ${status.currentTask.startedAt}`);
        }
        
        if (status.recentActivity.length > 0) {
          console.log(pc.bold('\nRecent Activity:'));
          status.recentActivity.forEach(activity => {
            console.log(`  ${activity.timestamp} - ${activity.type}: ${activity.description}`);
          });
        }
      } else {
        console.log(pc.yellow('● Inactive'));
        console.log(pc.dim('Claude Code is not currently running'));
      }
    }));

  // Setup hooks
  command
    .command('setup-hooks')
    .description('Setup or update Claude Code hooks for better integration')
    .option('--enhanced', 'Use enhanced hooks for full monitoring')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🔧 Setting up Claude Code hooks\n'));
      
      const controller = new ClaudeController();
      await controller.setupHooks(options.enhanced);
      
      console.log(pc.green('✅ Hooks configured successfully'));
      console.log(pc.dim('Check .claude/settings.local.json for details'));
    }));

  return command;
}